/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { detectCurrency, formatPrice, PLAN_LABELS } from "../lib/billing";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Check, Crown, CreditCard, Smartphone, Loader2, Sparkles, FileText } from "lucide-react";

function PlanCard({ plan, current, currency, fx, onChoose, busy }) {
  const isCurrent = current === plan.code;
  const highlight = plan.code === "premium" || plan.code === "clinique";
  return (
    <div data-testid={`plan-${plan.code}`}
      className={`rounded-3xl p-5 border ${highlight ? "bg-[#0A1426] text-white border-[#0A1426]" : "bg-white text-[#0A1426] border-[#E4E0D5]"}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-[Fraunces] text-xl font-bold">{plan.name}</div>
          <div className={`text-[12px] ${highlight ? "text-white/75" : "text-[#0A1426]/60"}`}>{plan.tagline}</div>
        </div>
        {highlight && <Crown size={22} className="text-[#D4AF5A]" />}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span data-testid={`plan-${plan.code}-price`} className="font-[Fraunces] text-3xl font-bold">{formatPrice(plan.price_usd, currency, fx)}</span>
        {plan.price_usd > 0 && <span className={`text-[12px] ${highlight ? "text-white/70" : "text-[#0A1426]/60"}`}>/ {plan.interval ? "mois" : "unité"}</span>}
      </div>
      {currency !== "USD" && plan.price_usd > 0 && (
        <div className={`text-[10px] mt-0.5 ${highlight ? "text-white/60" : "text-[#0A1426]/50"}`}>Facturé ${plan.price_usd.toFixed(2)} USD · équivalent indicatif</div>
      )}
      <ul className="mt-3 flex flex-col gap-1.5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-[13px]">
            <Check size={14} className={highlight ? "text-[#D4AF5A]" : "text-[#2BA3A0]"} /> {PLAN_LABELS[f] || f}
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <div data-testid={`plan-${plan.code}-current`} className={`mt-4 h-12 rounded-full grid place-items-center font-bold text-sm ${highlight ? "bg-white/15" : "bg-[#F5F3EE]"}`}>
          Votre forfait actuel
        </div>
      ) : plan.price_usd > 0 ? (
        <Button data-testid={`plan-${plan.code}-choose`} disabled={busy} onClick={() => onChoose(plan)}
          className={`w-full h-12 mt-4 rounded-full font-bold ${highlight ? "bg-[#D4AF5A] text-[#0A1426] hover:bg-white" : "bg-[#2BA3A0] text-white hover:bg-[#0A1426]"}`}>
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Choisir"}
        </Button>
      ) : null}
    </div>
  );
}

function PaymentSheet({ plan, currency, fx, onClose }) {
  const navigate = useNavigate();
  const { refreshBilling } = useApp();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [tel, setTel] = useState(user?.telephone || "");
  const [op, setOp] = useState("airtel");
  const [mm, setMm] = useState(null);

  const payStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { plan_code: plan.code, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const payMobile = async () => {
    if (!tel) { toast.error("Entrez votre numéro"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/mobile-money", { plan_code: plan.code, telephone: tel, operateur: op, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setMm(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const confirmMobile = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/mobile-money/${mm.session_id}/confirm`);
      await refreshBilling();
      toast.success("Paiement confirmé !");
      navigate("/payment/success?mm=1");
    } catch (e) { toast.error("Confirmation impossible"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0A1426]/60 flex items-end justify-center" onClick={onClose}>
      <div data-testid="payment-sheet" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[440px] bg-white rounded-t-3xl p-6 pb-8 flex flex-col gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Paiement</div>
          <div className="font-[Fraunces] text-xl font-bold text-[#0A1426]">{plan.name} — {formatPrice(plan.price_usd, currency, fx)}</div>
        </div>
        {!mm ? (
          <>
            <Button data-testid="pay-stripe-btn" disabled={busy} onClick={payStripe}
              className="h-14 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white font-bold justify-start px-5">
              <CreditCard size={20} className="mr-3" /> Carte bancaire (Stripe)
            </Button>
            {user?.region !== "europe" && <div className="rounded-2xl border border-[#E4E0D5] p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 font-bold text-[#0A1426]"><Smartphone size={18} /> Mobile Money</div>
              <div className="grid grid-cols-3 gap-2">
                {[["airtel", "Airtel Money"], ["mpesa", "M-Pesa"], ["orange", "Orange Money"]].map(([k, l]) => (
                  <button key={k} data-testid={`mm-op-${k}`} onClick={() => setOp(k)}
                    className={`h-10 rounded-full text-[12px] font-bold ${op === k ? "bg-[#0A1426] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}>{l}</button>
                ))}
              </div>
              <input data-testid="mm-phone-input" value={tel} onChange={(e) => setTel(e.target.value)} placeholder="+243 8xx xxx xxx"
                className="h-12 px-4 rounded-full border border-[#E4E0D5] text-[#0A1426] focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />
              <Button data-testid="pay-mobile-btn" disabled={busy} onClick={payMobile} variant="outline"
                className="h-12 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Payer par Mobile Money</Button>
              <p className="text-[10px] text-[#0A1426]/50">Paiement sécurisé via Flutterwave lorsque les clés sont configurées ; sinon mode démonstration (aucun débit réel).</p>
            </div>}
          </>
        ) : (
          <div data-testid="mm-pending" className="rounded-2xl bg-[#F5F3EE] p-4 flex flex-col gap-3">
            <p className="text-[14px] text-[#0A1426] font-medium">{mm.message}</p>
            <p className="text-[12px] text-[#0A1426]/70">Composez <b>*150#</b> sur votre téléphone puis saisissez votre PIN.</p>
            <Button data-testid="mm-confirm-btn" disabled={busy} onClick={confirmMobile} className="h-12 rounded-full bg-[#2BA3A0] text-white font-bold">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "J'ai confirmé sur mon téléphone"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SubscriptionPage({ audience: forcedAudience }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { billing, refreshBilling } = useApp();
  const [plans, setPlans] = useState([]);
  const [fx, setFx] = useState({});
  const [sheet, setSheet] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const currency = user?.region === "europe" ? "EUR" : detectCurrency();
  useEffect(() => { api.get("/billing/invoices").then(({ data }) => setInvoices(data)).catch(() => null); }, [billing]);
  const downloadInvoice = async (inv) => {
    try {
      const { data } = await api.get(`/billing/invoices/${inv.id}.pdf`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `${inv.numero}.pdf`; a.click();
    } catch (e) { toast.error("Téléchargement impossible"); }
  };
  const audience = forcedAudience || (user?.role === "therapeute" ? "pro" : "patient");

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => { setPlans(data.plans); setFx(data.fx); }).catch(() => null);
  }, []);

  const main = plans.filter((p) => p.audience === audience);
  const addons = plans.filter((p) => p.audience === "addon" && (audience === "pro" ? p.code === "formation_module" || p.code === "sms_pack_100" : p.code !== "formation_module"));

  const cancel = async () => {
    try { await api.post("/billing/cancel"); await refreshBilling(); toast.success("Abonnement résilié en fin de période"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-4" data-testid="subscription-page">
      <div className="flex items-center gap-3">
        <button data-testid="subscription-back" onClick={() => navigate(-1)} className="w-11 h-11 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]">
          <ArrowLeft size={20} />
        </button>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Abonnement</div>
          <div className="font-[Fraunces] text-xl font-bold text-[#0A1426]">Choisissez votre forfait</div>
        </div>
      </div>

      {billing && (
        <div data-testid="current-plan-card" className="bg-white rounded-3xl border border-[#E4E0D5] p-4 flex items-center gap-3">
          <Sparkles size={22} className="text-[#D4AF5A]" />
          <div className="flex-1">
            <div className="text-[12px] text-[#0A1426]/60">Forfait actuel</div>
            <div className="font-bold text-[#0A1426]">{billing.plan_name}</div>
            {billing.subscription?.current_period_end && (
              <div className="text-[11px] text-[#0A1426]/60">Jusqu'au {new Date(billing.subscription.current_period_end).toLocaleDateString("fr-FR")}</div>
            )}
          </div>
          <div className="text-right text-[11px] text-[#0A1426]/70">
            {billing.credits.teleconsult > 0 && <div>{billing.credits.teleconsult} téléconsult.</div>}
            {billing.credits.sms > 0 && <div>{billing.credits.sms} SMS</div>}
            {billing.subscription && billing.subscription.price_usd !== 0 && billing.plan_code !== "gratuit" && (
              <button data-testid="cancel-subscription-btn" onClick={cancel} className="underline text-[#D9534F]">Résilier</button>
            )}
          </div>
        </div>
      )}

      <div className="text-[11px] text-[#0A1426]/60">Prix affichés en {currency} selon votre lieu de connexion · facturation en USD</div>

      <div className="flex flex-col gap-3">
        {main.map((p) => <PlanCard key={p.code} plan={p} current={billing?.plan_code} currency={currency} fx={fx} onChoose={setSheet} />)}
      </div>

      <h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold mt-2">À l'unité</h3>
      <div className="flex flex-col gap-3">
        {addons.map((p) => (
          <div key={p.code} data-testid={`addon-${p.code}`} className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex items-center gap-3">
            <div className="flex-1">
              <div className="font-bold text-[#0A1426]">{p.name}</div>
              <div className="text-[12px] text-[#0A1426]/60">{p.tagline}</div>
            </div>
            <Button data-testid={`addon-${p.code}-buy`} onClick={() => setSheet(p)} className="h-11 rounded-full bg-[#F5F3EE] text-[#0A1426] hover:bg-[#2BA3A0] hover:text-white font-bold px-4">
              {formatPrice(p.price_usd, currency, fx)}
            </Button>
          </div>
        ))}
      </div>

      {invoices.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E4E0D5] p-4" data-testid="invoices-card">
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-2">Factures</div>
          {invoices.slice(0, 12).map((inv) => (
            <div key={inv.id} data-testid={`invoice-${inv.numero}`} className="flex items-center justify-between gap-2 text-[12px] py-1.5 border-b border-[#F5F3EE] last:border-0 text-[#0A1426]">
              <div>
                <div className="font-bold">{inv.numero} · {inv.libelle}</div>
                <div className="text-[#0A1426]/60">{new Date(inv.date).toLocaleDateString("fr-FR")} · {inv.montant_usd.toFixed(2)} {inv.devise} · {inv.provider}</div>
              </div>
              <button data-testid={`invoice-${inv.numero}-pdf`} onClick={() => downloadInvoice(inv)} className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[#F5F3EE] font-bold text-[#0A1426]"><FileText size={14} /> PDF</button>
            </div>
          ))}
        </div>
      )}

      {sheet && <PaymentSheet plan={sheet} currency={currency} fx={fx} onClose={() => setSheet(null)} />}
    </div>
  );
}
